/**
 * Whose a tool is (ADR-0133).
 *
 * The platform's core is Google: Workspace, Maps and Places, Vertex and the
 * rest of Google Cloud, reached as the platform's own identity. Everything
 * else is a vendor's, whether it takes a key or not, and a vendor's tool
 * works only once a superadmin has admitted it and signed the assignment
 * that reaches it.
 *
 * The origin is read from where the manifest reaches, never written into
 * it. A field added to a manifest changes its canonical form and voids
 * every signature already made over it; the host is already signed.
 */
export const TOOL_ORIGINS = ['core', 'own', 'third_party'];
/** Google's API hosts. A host under one of these is Google core. */
const CORE_HOST_SUFFIXES = ['.googleapis.com'];
/**
 * Hosts that are ours. Empty today: every tool the platform runs reaches
 * Google or a vendor. A tool of our own is named here in the change that
 * adds it, which is where a reviewer sees it.
 */
const OWN_HOSTS = new Set();
/** Whose the tool at this origin is. Anything not Google's and not ours is a vendor's. */
export function toolOriginOf(origin) {
    let host;
    try {
        host = new URL(origin).hostname.toLowerCase();
    }
    catch {
        // An origin that does not parse is nobody's we trust.
        return 'third_party';
    }
    if (OWN_HOSTS.has(host))
        return 'own';
    if (CORE_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix)))
        return 'core';
    return 'third_party';
}
/** Whose a manifest's tool is, from the origin it is held to. */
export function toolOrigin(manifest) {
    return toolOriginOf(manifest.surface.origin);
}
/** Retired tool IDs cannot be revived by a persisted grant or a stale signature. */
export function retiredTool(id) {
    return ['executive', 'finance', 'food_science', 'fundraising', 'outbound', 'production']
        .some(department => id === `research.web_${department}`);
}
export const TOOL_REPLACEMENTS = {
    google_places: { ownEquivalent: 'Our own index of places, refreshed from Places only for what changed (ADR-0129)', effort: 'medium' },
    google_vertex: { ownEquivalent: 'Stays external: model inference is not ours to build; we route by region and cost instead', effort: 'n/a' },
    google_maps_js: { ownEquivalent: 'An open map renderer over tiles we serve ourselves', effort: 'high' },
    google_workspace: { ownEquivalent: 'Part of the Workspace the company runs on', effort: 'n/a' },
    hunter: { ownEquivalent: 'A pattern from our own approved contacts, checked by an SMTP probe', effort: 'low' },
    apollo: { ownEquivalent: 'Company facts from the EU registers and the company’s own site', effort: 'medium' },
    voyager: { ownEquivalent: 'Our own people index, from companies’ sites and approved contacts', effort: 'high' },
    customerio: { ownEquivalent: 'Our own sequence engine over Gmail drafts', effort: 'low' },
    cloudflare_browser: { ownEquivalent: 'A fetch of our own that honours robots.txt and returns markdown', effort: 'low' },
};
/**
 * The replacement threshold, in euros a month (ADR-0133, discovery round 3).
 * It is changed by a commit a person makes, so it is a decision in review
 * rather than a number that drifts.
 */
export const REPLACE_AT_EUR_MONTH = 50;
export const VENDORS_OUTSIDE_EU = [
    { vendor: 'apollo', name: 'Apollo', toolPrefix: 'outbound.apollo_' },
    { vendor: 'voyager', name: 'LinkedIn (Voyager)', toolPrefix: 'outbound.voyager_' },
    // A blocker notice to the superadmin's Telegram (ADR-0139): Telegram, outside the EU.
    { vendor: 'telegram', name: 'Telegram', toolPrefix: 'executive.telegram_' },
    // The superadmin's twin reading the platform's own repository (Amendment 0013, ADR-0172): GitHub, in the US.
    { vendor: 'github', name: 'GitHub', toolPrefix: 'tech.github_' },
    // ChatGPT, a hosted MCP client signed in as a person (ADR-0142): OpenAI, in the US.
    { vendor: 'openai', name: 'OpenAI (ChatGPT)', clients: true },
    // Claude on the web, desktop and phone, a hosted MCP client signed in as a
    // person (ADR-0171): Anthropic, in the US. Claude Code is not this: it
    // runs on the person's own machine and needs no admission.
    { vendor: 'anthropic', name: 'Anthropic (Claude)', clients: true },
];
/**
 * Metadata documents of clients that run on the person's own machine, read
 * without an admission because the address is this list's, never the
 * caller's (ADR-0171). A document here registers a loopback client only:
 * every redirect it names is http://localhost or http://127.0.0.1, so a
 * code can only go back to the machine that asked.
 */
export const LOOPBACK_CLIENT_DOCUMENTS = ['https://claude.ai/oauth/claude-code-client-metadata'];
/** The setup step that records a vendor's transfer paperwork. */
export const transferStepId = (vendor) => `vendors.transfer-${vendor}`;
/** The vendor outside the EU a tool belongs to, if it is one. Only vendors we call have a prefix. */
export function vendorOutsideEu(toolId) {
    const hit = VENDORS_OUTSIDE_EU.find((v) => (v.toolPrefix !== undefined && toolId.startsWith(v.toolPrefix)) || v.toolIds?.includes(toolId));
    return hit ? { vendor: hit.vendor, name: hit.name, stepId: transferStepId(hit.vendor) } : undefined;
}
/** The vendors whose hosted assistant may be admitted as a client of this platform (ADR-0142). */
export const HOSTED_CLIENT_VENDORS = VENDORS_OUTSIDE_EU.filter((v) => v.clients === true);
/** A hosted-client vendor by its id, with the step that records its transfer, or nothing. */
export function hostedClientVendor(vendor) {
    const hit = HOSTED_CLIENT_VENDORS.find((v) => v.vendor === vendor);
    return hit ? { vendor: hit.vendor, name: hit.name, stepId: transferStepId(hit.vendor) } : undefined;
}
//# sourceMappingURL=tool-origin.js.map